import autosize from 'autosize';

import Cart from './components/cart';
import { watchRebuyCart, refreshRebuyCart } from './components/rebuyCart';
import { enforceCartQuantityRules } from './components/subscriptionCartGuard';
import ContactForm from './components/contactForm';
import EqualHeight from './components/equalHeight';
import GiftSubscriptionForm from './components/giftSubscriptionForm';
import NewsletterSignup from './components/newsletterSignup';
import Producer from './components/producer';
import SharePopup from './components/sharePopup';
import Slider from './components/slider';
import SubscriptionBuilder from './components/subscriptionBuilder.svelte';

/* eslint-disable no-unused-vars */
const contactForm = new ContactForm();
const producer = new Producer();
const giftSubscriptionForm = new GiftSubscriptionForm();
const cart = new Cart();
/* eslint-enable no-unused-vars */

const slider = Slider.init();
EqualHeight.init();
NewsletterSignup.init();

const subscriptionBuilderContainer = document.querySelector('.js-subscription_builder');
if (subscriptionBuilderContainer) {
  // eslint-disable-next-line no-unused-vars
  const subscriptionBuilder = new SubscriptionBuilder({
    target: subscriptionBuilderContainer,
    props: {
      debug: subscriptionBuilderContainer.dataset.debug,
      recurring: subscriptionBuilderContainer.dataset.recurring,
    },
  });
}

// One cart watcher for the whole site. Rebuy's drawer opens on every page, so quantity
// rules have to be enforced everywhere — not just on the subscription builder. Components
// that care (the builder) listen for 'coava:cart-changed' rather than polling themselves.
async function handleCartChange(cart) {
  const corrected = await enforceCartQuantityRules(cart);
  // Pass the corrected cart straight through — setCartAsync needs it and this avoids a refetch.
  if (corrected) await refreshRebuyCart(corrected);
  document.dispatchEvent(new CustomEvent('coava:cart-changed', { detail: corrected || cart }));
}

// Backstop: polls only while Smart Cart is on screen.
watchRebuyCart(handleCartChange);

// Fast path for Rebuy → Smart Cart → Global Settings → Event Callbacks
// (LineItemIncrease / LineItemDecrease / LineItemRemoved), each calling:
//   window.coavaEnforceCartRules && window.coavaEnforceCartRules();
// Catches a change the moment it happens rather than on the next poll. Lives here rather
// than in the builder so it exists on every page, since the drawer opens everywhere.
window.coavaEnforceCartRules = async () => {
  try {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (res.ok) await handleCartChange(await res.json());
  } catch (err) {
    console.warn('[Coava] cart rule check failed:', err);
  }
};

// On Load
autosize(document.querySelectorAll('textarea'));

// Pick 3 random items from the grid
const gridItems = document.querySelectorAll('.js-pick .grid-item');
const itemsArray = Array.from(gridItems);
const numToKeep = 3;

// Shuffle array
for (let i = itemsArray.length - 1; i > 0; i--) {
  const j = Math.floor(Math.random() * (i + 1));
  [itemsArray[i], itemsArray[j]] = [itemsArray[j], itemsArray[i]];
}

// Remove all but first numToKeep items
itemsArray.slice(numToKeep).forEach(item => item.remove());

document.querySelectorAll('.js-share-popup').forEach(button => {
  button.addEventListener('click', (event) => {
    event.preventDefault();
    SharePopup.open(button.getAttribute('href'));
  });
});

window.setTimeout(() => {
  document.querySelector('html.wf-loading')?.classList.remove('wf-loading');
}, 3000);

document.querySelectorAll('.js-nav_toggle').forEach(toggle => {
  toggle.addEventListener('click', (event) => {
    event.preventDefault();
    document.body.classList.toggle('nav-active');
    toggle.textContent = toggle.textContent === 'Menu' ? 'Close' : 'Menu';
  });
});

document.querySelectorAll('.js-subheader').forEach(toggle => {
  const subheaderNavList = toggle.querySelector('.subheader-nav-list');

  const checkOverflow = () => {
    if (subheaderNavList.scrollWidth > subheaderNavList.clientWidth) {
      subheaderNavList.classList.add('is-overflowing');
    } else {
      subheaderNavList.classList.remove('is-overflowing');
    }
  };

  // Check on load
  checkOverflow();

  // Check on resize
  window.addEventListener('resize', checkOverflow);
});

document.querySelectorAll('.js-announcement-toggle').forEach(toggle => {
  toggle.addEventListener('click', (event) => {
    event.preventDefault();
    const announcement = document.querySelector('.js-announcement');
    const expandedElement = announcement.querySelector('.announcement-expanded');
    const isExpanded = announcement?.classList.toggle('is-expanded');
    toggle.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
    if (announcement) {
      announcement.setAttribute('id', 'announcement');
      expandedElement?.setAttribute('aria-hidden', isExpanded ? 'false' : 'true');
    }
  });
});

const activateContentToggle = (link) => {
  const activeClass = 'is-active';
  const parent = link.parentElement;
  const siblings = parent.querySelectorAll('button');
  siblings.forEach(sibling => sibling.classList.remove(activeClass));
  link.classList.add(activeClass);
  siblings.forEach(sibling => sibling.setAttribute('aria-selected', 'false'));
  const contentItems = Array.from(parent.parentElement.children).filter(el => !el.matches('.js-content_toggle'));
  contentItems.forEach(item => item.classList.remove(activeClass));
  contentItems[Array.from(siblings).indexOf(link)].classList.add(activeClass);

  // Set the aria-selected attribute on the button
  link.setAttribute('aria-selected', 'true');
};

document.querySelectorAll('.js-content_toggle button').forEach(link => {
  link.addEventListener('click', () => activateContentToggle(link));

  // Inactive panels are only visually hidden, so reveal one when keyboard focus lands inside it.
  const panel = document.getElementById(link.getAttribute('aria-controls'));
  panel?.addEventListener('focusin', () => {
    if (!panel.classList.contains('is-active')) activateContentToggle(link);
  });
});

document.querySelectorAll('.js-faq-toggle').forEach(button => {
  button.addEventListener('click', (event) => {
    event.preventDefault();
    const isExpanded = button.getAttribute('aria-expanded') === 'true';
    const answerId = button.getAttribute('aria-controls');
    const answerPanel = document.getElementById(answerId);

    if (answerPanel) {
      button.setAttribute('aria-expanded', !isExpanded ? 'true' : 'false');
      answerPanel.setAttribute('aria-hidden', !isExpanded ? 'false' : 'true');
      button.closest('.faq-question')?.classList.toggle('is-expanded', !isExpanded);
    }
  });
});

document.querySelectorAll('.image_w_text_card').forEach(card => {
  card.classList.remove('is-hidden');
});

document.querySelectorAll('.js-to_top').forEach(button => {
  button.addEventListener('click', (event) => {
    event.preventDefault();

    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });

    const focusableElements = document.body.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');

    // Focus the second focusable element (skip the skip to content link)
    focusableElements[1].focus();
  });
});

document.querySelectorAll('.js-animate_scroll').forEach(link => {
  link.addEventListener('click', (event) => {
    event.preventDefault();
    const target = document.querySelector(link.getAttribute('href'));
    const padding = link.dataset.scrollPadding || 0;
    if (target) {
      const top = target.getBoundingClientRect().top + window.pageYOffset - padding;
      window.scrollTo({
        top,
        behavior: 'smooth'
      });
    }
  });
});

document.querySelectorAll('.js-component_view-toggle').forEach(toggle => {
  toggle.addEventListener('click', (event) => {
    event.preventDefault();
    document.querySelectorAll('.js-component_view').forEach(view => view.classList.toggle('is-hidden'));
    toggle.closest('.js-component_view')?.classList.toggle('is-active');
    const id = toggle.closest('.component_library-heading')?.id;
    setTimeout(() => {
      window.location.hash = id;
    }, 50);
  });
});

document.querySelectorAll('.js-show_all_siblings').forEach(button => {
  button.addEventListener('click', (event) => {
    event.preventDefault();
    button.style.display = 'none';
    Array.from(button.parentElement.children)
      .filter(sibling => sibling !== button)
      .forEach(sibling => sibling.classList.remove('is-hidden'));
  });
});

// If the current domain isn't https://coavacoffee.com or shop.coavacoffee.com, make sure folks confirm that they want to leave the site if clicking a link to an external site.
// if (window.location.hostname !== 'coavacoffee.com' && window.location.hostname !== 'shop.coavacoffee.com') {
//   document.querySelectorAll('a').forEach(link => {
//     if (
//       link.href.startsWith('http') &&
//       new URL(link.href).hostname !== window.location.hostname
//     ) {
//       link.addEventListener('click', (event) => {
//         event.preventDefault();
//         const confirm = window.confirm('Are you sure you want to leave the site?');
//         if (confirm) {
//           window.location.href = link.href;
//         }
//       });
//     }
//   });
// }
